import { useApolloClient } from "@apollo/client";
import {
  Box,
  Button,
  Checkbox,
  Collapsible,
  Field,
  Input,
  NativeSelect,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { LuChevronDown, LuChevronUp, LuInfo } from "react-icons/lu";
import {
  type ExhibitionRakutenSettingFragment,
  ListRakutenGenresDocument,
  useGetRakutenShippingInfoQuery,
  useListRakutenCategoriesQuery,
  useListRakutenGenresQuery,
} from "../graphql/generated/apollo";
import { useShopContext } from "../shop";
import { timestampToDateTime } from "../utils/date";
import { OptionalTag } from "./ItemForm/OptionalTag";
import { Tooltip } from "./ui/tooltip";

type RakutenExhibitionSettingsFormProps = {
  rakutenExhibitionSettings: ExhibitionRakutenSettingFragment;
  onChange: (rakutenExhibitionSettings: ExhibitionRakutenSettingFragment) => void;
};
export function RakutenExhibitionSettingsForm(props: RakutenExhibitionSettingsFormProps) {
  const { rakutenExhibitionSettings, onChange } = props;
  const currentGenreId = rakutenExhibitionSettings.genre_id;
  const currentCategoryIds = rakutenExhibitionSettings.rakuten_categories ?? ["1"];

  if (!rakutenExhibitionSettings) {
    return <Spinner />;
  }
  const { currentShopId } = useShopContext();
  if (!currentShopId) {
    return <Spinner />;
  }

  return (
    <VStack>
      <Field.Root>
        <Field.Label>
          <Text fontSize="sm">ジャンル</Text>
        </Field.Label>
        <RakutenGenreSelector
          shopId={currentShopId}
          initialGenreId={currentGenreId}
          onChange={(genre) => {
            onChange({
              ...rakutenExhibitionSettings,
              genre_id: genre ? genre.toString() : "",
            });
          }}
        />
      </Field.Root>
      <Field.Root>
        <Field.Label>
          <Text fontSize="sm">
            キャッチコピー
            <OptionalTag ml={2} />
          </Text>
        </Field.Label>
        <Input
          value={rakutenExhibitionSettings.tagline ?? ""}
          onChange={(e) => {
            onChange({
              ...rakutenExhibitionSettings,
              tagline: e.target.value,
            });
          }}
          placeholder="キャッチコピー"
          maxLength={58}
        />
        <Text fontSize="xs" color="gray.500" mt={1} textAlign="right">
          {rakutenExhibitionSettings.tagline?.length ?? 0}/58文字
          <Tooltip
            content={
              <Text>
                文字数上限についての注意事項
                <br />
                全角58文字まで
              </Text>
            }
          >
            <span>
              <LuInfo />
            </span>
          </Tooltip>
        </Text>
      </Field.Root>
      <Text fontSize="sm" textAlign="start">
        カテゴリ　※最大5つまで選択可能
        <OptionalTag ml={2} />
      </Text>
      <RakutenCategorySelector
        shopId={currentShopId}
        initialCategoryIds={currentCategoryIds}
        onChange={(categoryIds) => {
          onChange({
            ...rakutenExhibitionSettings,
            rakuten_categories: categoryIds,
          });
        }}
      />
      <Field.Root>
        <Field.Label>
          <Text fontSize="sm">
            配送方法セット
            <OptionalTag ml={2} />
          </Text>
        </Field.Label>
        <RakutenShippingMethodGroupSelector
          shopId={currentShopId}
          value={rakutenExhibitionSettings.shipping_method_group?.toString() ?? ""}
          onChange={(value) => {
            onChange({
              ...rakutenExhibitionSettings,
              shipping_method_group: value ? Number(value) : null,
            });
          }}
        />
      </Field.Root>
      <Field.Root>
        <Field.Label>
          <Text fontSize="sm">納期情報の表示</Text>
        </Field.Label>
        <VStack gap={4} align="stretch">
          <Field.Root>
            <Field.Label>
              <Text fontSize="sm">
                在庫あり時
                <OptionalTag ml={2} />
              </Text>
            </Field.Label>
            <RakutenDeliveryDateSelector
              shopId={currentShopId}
              value={rakutenExhibitionSettings.normal_delivery_date_id?.toString() ?? ""}
              onChange={(value) => {
                onChange({
                  ...rakutenExhibitionSettings,
                  normal_delivery_date_id: value ? Number(value) : null,
                });
              }}
            />
          </Field.Root>
          <Field.Root>
            <Field.Label>
              <Text fontSize="sm">
                在庫切れ時
                <OptionalTag ml={2} />
              </Text>
            </Field.Label>
            <RakutenDeliveryDateSelector
              shopId={currentShopId}
              value={rakutenExhibitionSettings.backorder_delivery_date_id?.toString() ?? ""}
              onChange={(value) => {
                onChange({
                  ...rakutenExhibitionSettings,
                  backorder_delivery_date_id: value ? Number(value) : null,
                });
              }}
            />
          </Field.Root>
        </VStack>
      </Field.Root>
      <Field.Root>
        <Field.Label>
          <Text fontSize="sm">
            在庫表示設定
            <OptionalTag ml={2} />
          </Text>
        </Field.Label>
        <NativeSelect.Root>
          <NativeSelect.Field
            value={rakutenExhibitionSettings.inventory_display ?? "HIDDEN_STOCK"}
            onChange={(e) => {
              const value = e.target.value;
              onChange({
                ...rakutenExhibitionSettings,
                inventory_display: value,
                // DISPLAY_LOW_STOCK以外が選択された場合は閾値をリセット
                low_stock_threshold:
                  value === "DISPLAY_LOW_STOCK" ? rakutenExhibitionSettings.low_stock_threshold : undefined,
              });
            }}
          >
            <option value="HIDDEN_STOCK">非表示</option>
            <option value="DISPLAY_ABSOLUTE_STOCK_COUNT">表示</option>
            <option value="DISPLAY_LOW_STOCK">残り在庫数表示閾値より小さい</option>
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field.Root>

      {rakutenExhibitionSettings.inventory_display === "DISPLAY_LOW_STOCK" && (
        <Field.Root>
          <Field.Label>
            <Text fontSize="sm">残り在庫数表示閾値</Text>
          </Field.Label>
          <Input
            type="number"
            min={1}
            value={rakutenExhibitionSettings.low_stock_threshold ?? ""}
            onChange={(e) => {
              const value = e.target.value ? Number.parseInt(e.target.value, 10) : undefined;
              onChange({
                ...rakutenExhibitionSettings,
                low_stock_threshold: value,
              });
            }}
          />
        </Field.Root>
      )}
      <Field.Root>
        <Field.Label>
          <Text fontSize="sm">
            在庫あり時の出荷リードタイム
            <OptionalTag ml={2} />
          </Text>
        </Field.Label>
        <RakutenOperationLeadTimeSelector
          shopId={currentShopId}
          value={rakutenExhibitionSettings.operation_normal_delivery_time_id?.toString() ?? ""}
          onChange={(value) => {
            onChange({
              ...rakutenExhibitionSettings,
              operation_normal_delivery_time_id: value ? Number(value) : null,
            });
          }}
        />
      </Field.Root>
      <Field.Root>
        <Field.Label>
          <Text fontSize="sm">
            在庫切れ時の出荷リードタイム
            <OptionalTag ml={2} />
          </Text>
        </Field.Label>
        <RakutenOperationLeadTimeSelector
          shopId={currentShopId}
          value={rakutenExhibitionSettings.operation_backorder_delivery_time_id?.toString() ?? ""}
          onChange={(value) => {
            onChange({
              ...rakutenExhibitionSettings,
              operation_backorder_delivery_time_id: value ? Number(value) : null,
            });
          }}
        />
      </Field.Root>
      <Field.Root>
        <Field.Label>
          <Text fontSize="sm">
            配送元
            <OptionalTag ml={2} />
          </Text>
        </Field.Label>
        <RakutenShipFromSelector
          shopId={currentShopId}
          value={rakutenExhibitionSettings.ship_from_id?.toString() ?? ""}
          onChange={(value) => {
            onChange({
              ...rakutenExhibitionSettings,
              ship_from_id: value ? Number(value) : null,
            });
          }}
        />
      </Field.Root>
      <Field.Root>
        <Field.Label>
          <Text fontSize="sm">
            ポイント変倍率
            <OptionalTag ml={2} />
          </Text>
        </Field.Label>
        <NativeSelect.Root>
          <NativeSelect.Field
            value={rakutenExhibitionSettings.point_multiplier_rate?.toString() ?? ""}
            onChange={(e) => {
              const value = e.target.value ? Number(e.target.value) : null;
              onChange({
                ...rakutenExhibitionSettings,
                point_multiplier_rate: value,
              });
            }}
          >
            <option value="">選択してください</option>
            {Array.from({ length: 19 }, (_, i) => i + 2).map((rate) => (
              <option key={rate} value={rate}>
                {rate}倍
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
        <Text fontSize="xs" color="gray.500" mt={1}>
          2倍～20倍の範囲で選択可能
        </Text>
      </Field.Root>
      <Field.Root>
        <Field.Label>
          <Text fontSize="sm">
            ポイント変倍適用期間　開始日時
            <OptionalTag ml={2} />
          </Text>
        </Field.Label>
        <Input
          type="datetime-local"
          value={
            rakutenExhibitionSettings.point_multiplier_starts_at
              ? timestampToDateTime(rakutenExhibitionSettings.point_multiplier_starts_at)
              : ""
          }
          onChange={(e) => {
            onChange({
              ...rakutenExhibitionSettings,
              point_multiplier_starts_at: e.target.value || null,
            });
          }}
        />
      </Field.Root>
      <Field.Root>
        <Field.Label>
          <Text fontSize="sm">
            ポイント変倍適用期間　終了日時
            <OptionalTag ml={2} />
          </Text>
        </Field.Label>
        <Input
          type="datetime-local"
          value={
            rakutenExhibitionSettings.point_multiplier_ends_at
              ? timestampToDateTime(rakutenExhibitionSettings.point_multiplier_ends_at)
              : ""
          }
          onChange={(e) => {
            onChange({
              ...rakutenExhibitionSettings,
              point_multiplier_ends_at: e.target.value || null,
            });
          }}
        />
      </Field.Root>
    </VStack>
  );
}

type RakutenGenreSelectorProps = {
  shopId: string;
  initialGenreId?: string;
  onChange: (genreId: number | "") => void;
};

export function RakutenGenreSelector({ shopId, initialGenreId, onChange }: RakutenGenreSelectorProps) {
  const client = useApolloClient();
  const [selectedGenres, setSelectedGenres] = useState<{ genreId: number; name: string; lowest: boolean }[]>([]);
  const didRunInitialLoad = useRef(false);
  // ✅ 初期 genrePath の取得と1階層ずつセット
  useEffect(() => {
    if (!initialGenreId || didRunInitialLoad.current) return;

    didRunInitialLoad.current = true;

    (async () => {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const { data } = await client.query({
        query: ListRakutenGenresDocument,
        variables: {
          input: {
            shopId,
            parentGenreId: Number(initialGenreId),
          },
        },
        fetchPolicy: "network-only",
      });

      const path = data?.listRakutenGenres?.genrePath ?? [];

      for (let i = 0; i < path.length; i++) {
        await new Promise((r) => setTimeout(r, 500)); // 💡 QPS制限対策
        setSelectedGenres((prev) => [...prev.slice(0, i), path[i]]);

        if (i === path.length - 1) {
          onChange(path[i].genreId);
        } else {
          onChange("");
        }
      }
    })();
  }, []);

  const handleSelect = (depth: number, genreId?: number, name?: string, lowest?: boolean) => {
    if (!genreId) {
      setSelectedGenres(selectedGenres.slice(0, depth));
      onChange("");
      return;
    }

    const newPath = [...selectedGenres.slice(0, depth), { genreId, name: name ?? "", lowest: lowest ?? false }];
    setSelectedGenres(newPath);

    if (lowest) {
      onChange(genreId);
    } else {
      onChange("");
    }
  };

  return (
    <>
      {Array.from({ length: selectedGenres.length + 1 }).map((_, depth) => (
        <GenreSelect
          key={depth}
          shopId={shopId}
          parentGenreId={depth === 0 ? 0 : selectedGenres[depth - 1]?.genreId}
          selectedGenreId={selectedGenres[depth]?.genreId}
          depth={depth}
          onSelect={handleSelect}
        />
      ))}

      <div>現在のジャンル: {selectedGenres.map((g) => g.name).join(" > ") || "未選択"}</div>
    </>
  );
}

type GenreSelectProps = {
  shopId: string;
  parentGenreId?: number;
  selectedGenreId?: number;
  depth: number;
  onSelect: (depth: number, genreId?: number, name?: string, lowest?: boolean) => void;
};

function GenreSelect({ shopId, parentGenreId = 0, selectedGenreId, depth, onSelect }: GenreSelectProps) {
  const { data, loading, error } = useListRakutenGenresQuery({
    variables: {
      input: {
        shopId,
        parentGenreId,
      },
    },
  });

  const genres = (data?.listRakutenGenres?.childGenres ?? []).filter(
    (genre): genre is NonNullable<typeof genre> => genre !== null,
  );

  if (loading) return null;
  if (error) return <div>Error: {error.message}</div>;
  if (!genres.length) return null;

  return (
    <NativeSelect.Root>
      <NativeSelect.Field
        value={selectedGenreId?.toString() ?? ""}
        onChange={(e) => {
          const genreId = e.target.value ? Number(e.target.value) : undefined;
          const selectedGenre = genres.find((g) => g.genreId === genreId);
          onSelect(depth, genreId, selectedGenre?.name, selectedGenre?.lowest);
        }}
      >
        <option value="">選択してください</option>
        {genres.map((genre) => (
          <option key={genre.genreId} value={genre.genreId}>
            {genre.name}
          </option>
        ))}
      </NativeSelect.Field>
      <NativeSelect.Indicator />
    </NativeSelect.Root>
  );
}

type RakutenShippingMethodGroupSelectorProps = {
  shopId: string;
  value: string;
  onChange: (value: string) => void;
};

function RakutenShippingMethodGroupSelector({ shopId, value, onChange }: RakutenShippingMethodGroupSelectorProps) {
  const { data, loading, error } = useGetRakutenShippingInfoQuery({
    variables: {
      input: { shopId },
    },
  });

  if (loading) return <Spinner />;
  if (error) return <Text color="red.500">エラー: {error.message}</Text>;

  const options = data?.getRakutenShippingInfo.shippingMethodGroups ?? [];

  return (
    <NativeSelect.Root>
      <NativeSelect.Field
        placeholder="選択してください"
        value={value}
        onChange={(e: { target: { value: string } }) => onChange(e.target.value)}
      >
        <option value="">選択してください</option>
        {options.map((group: { id: any; name: any; isDefault: any }) => (
          <option key={group.id} value={group.id}>
            {group.name} {group.isDefault ? "（デフォルト）" : ""}
          </option>
        ))}
      </NativeSelect.Field>
      <NativeSelect.Indicator />
    </NativeSelect.Root>
  );
}

type RakutenDeliveryDateSelectorProps = {
  shopId: string;
  value: string;
  onChange: (value: string) => void;
};

function RakutenDeliveryDateSelector({ shopId, value, onChange }: RakutenDeliveryDateSelectorProps) {
  const { data, loading, error } = useGetRakutenShippingInfoQuery({
    variables: {
      input: { shopId },
    },
  });

  if (loading) return <Spinner />;
  if (error) return <Text color="red.500">エラー: {error.message}</Text>;

  const options = data?.getRakutenShippingInfo.deliveryDates ?? [];

  return (
    <NativeSelect.Root>
      <NativeSelect.Field
        placeholder="選択してください"
        value={value}
        onChange={(e: { target: { value: string } }) => onChange(e.target.value)}
      >
        <option value="">選択してください</option>
        {options.map((group: { id: any; caption: any }) => (
          <option key={group.id} value={group.id}>
            {`ID:${group.id} ${group.caption}`}
          </option>
        ))}
      </NativeSelect.Field>
      <NativeSelect.Indicator />
    </NativeSelect.Root>
  );
}

type RakutenOperationLeadTimeSelectorProps = {
  shopId: string;
  value: string;
  onChange: (value: string) => void;
};

function RakutenOperationLeadTimeSelector({ shopId, value, onChange }: RakutenOperationLeadTimeSelectorProps) {
  const { data, loading, error } = useGetRakutenShippingInfoQuery({
    variables: {
      input: { shopId },
    },
  });

  if (loading) return <Spinner />;
  if (error) return <Text color="red.500">エラー: {error.message}</Text>;

  const options = data?.getRakutenShippingInfo.operationLeadTimes ?? [];

  return (
    <NativeSelect.Root>
      <NativeSelect.Field
        placeholder="選択してください"
        value={value}
        onChange={(e: { target: { value: string } }) => onChange(e.target.value)}
      >
        <option value="">選択してください</option>
        {options.map((group: { id: any; name: any }) => (
          <option key={group.id} value={group.id}>
            {`ID:${group.id} ${group.name}`}
          </option>
        ))}
      </NativeSelect.Field>
      <NativeSelect.Indicator />
    </NativeSelect.Root>
  );
}

type RakutenShipFromSelectorProps = {
  shopId: string;
  value: string;
  onChange: (value: string) => void;
};

function RakutenShipFromSelector({ shopId, value, onChange }: RakutenShipFromSelectorProps) {
  const { data, loading, error } = useGetRakutenShippingInfoQuery({
    variables: {
      input: { shopId },
    },
  });

  if (loading) return <Spinner />;
  if (error) return <Text color="red.500">エラー: {error.message}</Text>;

  const options = data?.getRakutenShippingInfo.shipFroms ?? [];

  return (
    <NativeSelect.Root>
      <NativeSelect.Field
        placeholder="選択してください"
        value={value}
        onChange={(e: { target: { value: string } }) => onChange(e.target.value)}
      >
        <option value="">選択してください</option>
        {options.map((group: { id: any; name: any }) => (
          <option key={group.id} value={group.id}>
            {`ID:${group.id} ${group.name}`}
          </option>
        ))}
      </NativeSelect.Field>
      <NativeSelect.Indicator />
    </NativeSelect.Root>
  );
}

type RakutenCategorySelectorProps = {
  shopId: string;
  initialCategoryIds?: string[];
  onChange: (categoryIds: string[]) => void;
};

function RakutenCategorySelector({ shopId, initialCategoryIds = [], onChange }: RakutenCategorySelectorProps) {
  // 初期値が空の場合は「その他」カテゴリを追加
  const [selectedIds, setSelectedIds] = useState<string[]>(
    initialCategoryIds.length === 0 ? ["1"] : initialCategoryIds,
  );
  const [isOpen, setIsOpen] = useState(false);
  const { data, loading, error } = useListRakutenCategoriesQuery({
    variables: {
      input: { shopId, parentCategoryId: "0" },
    },
  });

  if (loading) return <Spinner />;
  if (error) return <Text color="red.500">エラー: {error.message}</Text>;

  type Category = {
    categoryId: string;
    name: string;
  };

  const options = (data?.listRakutenCategories.childCategories ?? []).filter(
    (category): category is NonNullable<typeof category> & Category =>
      category !== null && "categoryId" in category && "name" in category && category.categoryId !== "1",
  );

  const handleCheckboxChange = (categoryId: string) => {
    const isSelected = selectedIds.includes(categoryId);
    const isOtherCategory = categoryId === "1";

    let newSelectedIds: string[];
    if (isSelected) {
      if (isOtherCategory) {
        return;
      }
      newSelectedIds = selectedIds.filter((id: string) => id !== categoryId);
      if (newSelectedIds.length === 0) {
        newSelectedIds = ["1"];
      }
    } else {
      if (selectedIds.length >= 5) {
        return;
      }
      newSelectedIds = [...selectedIds.filter((id: string) => id !== "1"), categoryId];
    }

    setSelectedIds(newSelectedIds);
    onChange(newSelectedIds);
  };

  const otherCategoryName =
    data?.listRakutenCategories.childCategories?.find(
      (category): category is NonNullable<typeof category> & Category =>
        category !== null && category.categoryId === "1",
    )?.name ?? "その他";

  const selectedCategories = selectedIds.includes("1")
    ? otherCategoryName
    : options
        .filter((category: Category) => selectedIds.includes(category.categoryId))
        .map((category: Category) => category.name)
        .join(", ");

  return (
    <Box w="100%">
      <Button
        onClick={() => setIsOpen(!isOpen)}
        w="100%"
        justifyContent="space-between"
        mb={2}
        variant="outline"
        css={{
          "& > span": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "calc(100% - 40px)",
          },
        }}
      >
        {selectedCategories || "カテゴリを選択"}
        {isOpen ? <LuChevronUp /> : <LuChevronDown />}
      </Button>
      <Text fontSize="sm" color="gray.600" mb={2}>
        選択中: {selectedIds.filter((id: string) => id !== "1").length}/5
      </Text>
      <Collapsible.Root open={isOpen}>
        <Collapsible.Content>
          <Box border="1px" borderColor="gray.200" borderRadius="md" p={4} maxH="300px" overflowY="auto">
            <VStack align="start" gap={2} w="100%">
              {selectedIds.includes("1") && (
                <Checkbox.Root
                  key="1"
                  checked={true}
                  disabled={true}
                  w="100%"
                  css={{
                    "& > span:last-of-type": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "100%",
                    },
                  }}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label>{otherCategoryName}</Checkbox.Label>
                </Checkbox.Root>
              )}
              {options.map((category: { categoryId: string; name: string }) => (
                <Checkbox.Root
                  key={category.categoryId}
                  checked={selectedIds.includes(category.categoryId)}
                  onCheckedChange={() => handleCheckboxChange(category.categoryId)}
                  w="100%"
                  disabled={
                    !selectedIds.includes(category.categoryId) &&
                    selectedIds.filter((id: string) => id !== "1").length >= 5
                  }
                  css={{
                    "& > span:last-of-type": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "100%",
                    },
                  }}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label>{category.name}</Checkbox.Label>
                </Checkbox.Root>
              ))}
            </VStack>
          </Box>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
}
